/**
 * Muscle taxonomy.
 *
 * `recoveryHours` is how long a muscle typically needs after a hard, direct
 * session before it can take another one productively. It drives the freshness
 * meter and the "what should I train next" recommendation — small muscles that
 * recover fast (abs, calves, forearms) can be hit far more often than the big
 * ones. These are training-practice defaults, not lab numbers; tune them in
 * Settings if your own recovery runs faster or slower.
 *
 * `weeklySets` is the min/max of *direct* working sets per week that keeps a
 * muscle growing without burying it. Indirect work (triceps on a bench press)
 * counts at a discount — see SECONDARY_CREDIT.
 */

export type MuscleId =
  | 'chest'
  | 'delts_front'
  | 'delts_side'
  | 'delts_rear'
  | 'lats'
  | 'upper_back'
  | 'traps'
  | 'lower_back'
  | 'biceps'
  | 'triceps'
  | 'forearms'
  | 'quads'
  | 'hamstrings'
  | 'glutes'
  | 'calves'
  | 'adductors'
  | 'abs'
  | 'obliques';

export type Region = 'push' | 'pull' | 'legs' | 'core';

export interface Muscle {
  id: MuscleId;
  name: string;
  short: string;
  region: Region;
  recoveryHours: number;
  weeklySets: [number, number];
  /** Rough share of total body muscle mass, used to weight the "staleness" score. */
  size: 'large' | 'medium' | 'small';
}

/** A set for a secondary muscle counts as this fraction of a direct set. */
export const SECONDARY_CREDIT = 0.5;

export const MUSCLES: Record<MuscleId, Muscle> = {
  chest: {
    id: 'chest',
    name: 'Chest (pectorals)',
    short: 'Chest',
    region: 'push',
    recoveryHours: 72,
    weeklySets: [10, 20],
    size: 'large',
  },
  delts_front: {
    id: 'delts_front',
    name: 'Front delts',
    short: 'Front delt',
    region: 'push',
    recoveryHours: 48,
    weeklySets: [4, 12],
    size: 'small',
  },
  delts_side: {
    id: 'delts_side',
    name: 'Side delts',
    short: 'Side delt',
    region: 'push',
    recoveryHours: 48,
    weeklySets: [8, 22],
    size: 'small',
  },
  delts_rear: {
    id: 'delts_rear',
    name: 'Rear delts',
    short: 'Rear delt',
    region: 'pull',
    recoveryHours: 48,
    weeklySets: [6, 18],
    size: 'small',
  },
  lats: {
    id: 'lats',
    name: 'Lats',
    short: 'Lats',
    region: 'pull',
    recoveryHours: 72,
    weeklySets: [10, 20],
    size: 'large',
  },
  upper_back: {
    id: 'upper_back',
    name: 'Upper back (rhomboids, mid-traps)',
    short: 'Upper back',
    region: 'pull',
    recoveryHours: 60,
    weeklySets: [8, 18],
    size: 'medium',
  },
  traps: {
    id: 'traps',
    name: 'Traps (upper)',
    short: 'Traps',
    region: 'pull',
    recoveryHours: 48,
    weeklySets: [4, 14],
    size: 'medium',
  },
  lower_back: {
    id: 'lower_back',
    name: 'Lower back (erectors)',
    short: 'Lower back',
    region: 'pull',
    recoveryHours: 72,
    weeklySets: [2, 8],
    size: 'medium',
  },
  biceps: {
    id: 'biceps',
    name: 'Biceps',
    short: 'Biceps',
    region: 'pull',
    recoveryHours: 48,
    weeklySets: [8, 20],
    size: 'small',
  },
  triceps: {
    id: 'triceps',
    name: 'Triceps',
    short: 'Triceps',
    region: 'push',
    recoveryHours: 48,
    weeklySets: [8, 20],
    size: 'medium',
  },
  forearms: {
    id: 'forearms',
    name: 'Forearms',
    short: 'Forearms',
    region: 'pull',
    recoveryHours: 36,
    weeklySets: [4, 14],
    size: 'small',
  },
  quads: {
    id: 'quads',
    name: 'Quads',
    short: 'Quads',
    region: 'legs',
    recoveryHours: 72,
    weeklySets: [8, 18],
    size: 'large',
  },
  hamstrings: {
    id: 'hamstrings',
    name: 'Hamstrings',
    short: 'Hams',
    region: 'legs',
    recoveryHours: 72,
    weeklySets: [8, 16],
    size: 'large',
  },
  glutes: {
    id: 'glutes',
    name: 'Glutes',
    short: 'Glutes',
    region: 'legs',
    recoveryHours: 60,
    weeklySets: [6, 16],
    size: 'large',
  },
  calves: {
    id: 'calves',
    name: 'Calves',
    short: 'Calves',
    region: 'legs',
    recoveryHours: 36,
    weeklySets: [6, 20],
    size: 'small',
  },
  adductors: {
    id: 'adductors',
    name: 'Adductors',
    short: 'Adductors',
    region: 'legs',
    recoveryHours: 60,
    weeklySets: [2, 8],
    size: 'medium',
  },
  abs: {
    id: 'abs',
    name: 'Abs (rectus abdominis)',
    short: 'Abs',
    region: 'core',
    recoveryHours: 24,
    weeklySets: [8, 20],
    size: 'small',
  },
  obliques: {
    id: 'obliques',
    name: 'Obliques',
    short: 'Obliques',
    region: 'core',
    recoveryHours: 24,
    weeklySets: [4, 12],
    size: 'small',
  },
};

export const MUSCLE_IDS = Object.keys(MUSCLES) as MuscleId[];

export const REGION_LABEL: Record<Region, string> = {
  push: 'Push',
  pull: 'Pull',
  legs: 'Legs',
  core: 'Core',
};

export function musclesInRegion(region: Region): Muscle[] {
  return MUSCLE_IDS.map((id) => MUSCLES[id]).filter((m) => m.region === region);
}
